// =============================================================================
// videoSource — URL detection for the popup's video / shorts renderers
// =============================================================================
//
// Authors paste any of three URL shapes into the videoUrl field of a
// SHORT_VIDEO or SHORTS body:
//
//   1. YouTube watch URL          https://www.youtube.com/watch?v=<id>
//      YouTube short URL          https://youtu.be/<id>
//      YouTube Shorts URL         https://www.youtube.com/shorts/<id>
//   2. Cinema8 interactive URL    https://cinema8.com/video/<id>
//      Cinema8 site URL           https://cinema8.site/video/<id>
//      Cinema8 raw media URL      https://cinema8.com/raw-video/<id>
//   3. Direct file URL            https://cdn.example.com/path/clip.mp4
//
// This module recognises which one and returns a tagged union so the
// renderer can pick the right element (native <video>, YouTube iframe,
// <cinema8-player> web component, or the Cinema8 raw-video iframe).
//
// Why not reuse EmbeddedVideo from the regular blocks layer:
//   - EmbeddedVideo embeds its own chrome (caption, aspect-ratio box).
//   - The popup has its own chrome (KindBadge, PopupHeader, completion
//     handler). Mixing them produces nested aspect boxes and double
//     captions.
//   - The popup needs the onEnded callback wired to onComplete; the
//     EmbeddedVideo block doesn't expose one.
//
// Tracking is off by default — embeds use youtube-nocookie.com.
// =============================================================================

export type VideoSource =
  | { type: 'youtube'; embedUrl: string; watchUrl: string }
  /** {@code raw}: a raw media hash ({@code /raw-video/}), not an interactive scene. */
  | { type: 'cinema8'; mediaId: string; raw: boolean }
  | { type: 'native'; url: string }
  | { type: 'unknown'; url?: string };

/**
 * Detect the provider behind a video URL. Returns 'unknown' for empty
 * strings, malformed URLs, or any host we don't have a player for.
 */
export function parseVideoSource(url: string | undefined): VideoSource {
  if (!url) return { type: 'unknown' };

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { type: 'unknown', url };
  }

  const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

  // YouTube canonical: youtube.com/watch?v=<id>
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    // YouTube Shorts: youtube.com/shorts/<id> — different path, same embed
    const shortsMatch = parsed.pathname.match(/^\/shorts\/([A-Za-z0-9_-]+)/);
    if (shortsMatch) {
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube-nocookie.com/embed/${shortsMatch[1]}`,
        watchUrl: url,
      };
    }
    // Regular watch URL: ?v=<id>
    const id = parsed.searchParams.get('v');
    if (id) {
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube-nocookie.com/embed/${id}`,
        watchUrl: url,
      };
    }
  }

  // YouTube short URL: youtu.be/<id>
  if (host === 'youtu.be') {
    const id = parsed.pathname.slice(1).split('/')[0];
    if (id) {
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube-nocookie.com/embed/${id}`,
        watchUrl: url,
      };
    }
  }

  // Cinema8: cinema8.com/video/<id>, cinema8.site/video/<id> or
  // cinema8.com/raw-video/<id>. Both apex and *.cinema8.* subdomains are accepted.
  if (
    host === 'cinema8.com' ||
    host === 'cinema8.site' ||
    host.endsWith('.cinema8.com') ||
    host.endsWith('.cinema8.site')
  ) {
    const m = parsed.pathname.match(/\/(raw-video|video)\/([A-Za-z0-9]+)/);
    if (m) return { type: 'cinema8', mediaId: m[2]!, raw: m[1] === 'raw-video' };
  }

  // Native file extensions (mp4, webm, m4v, ogv, mov). We accept anything
  // that ends in a known video extension regardless of host.
  if (/\.(mp4|webm|m4v|ogv|mov)(\?.*)?$/i.test(parsed.pathname)) {
    return { type: 'native', url };
  }

  // Unknown host with no clear file extension. We could try native
  // playback as a last resort, but it'd 99% fail and the user gets a
  // confusing broken video element. Better to show an explicit fallback.
  return { type: 'unknown', url };
}
